// Login-response flag storage. The `POST /login` endpoint returns
// `isVerified` and `isResetPassword` (both `0 | 1`) alongside the access
// token. These drive client-side guards — e.g. forcing the user through
// `/reset-password` when `isVerified === 0` — and need to be readable
// synchronously after a page reload.
//
// Kept separate from `tokenStorage` because the flags are auth *meta*,
// not credentials, and from `userStorage` because they aren't part of
// the cached `/users/me` payload.

export const IS_VERIFIED_KEY = 'isVerified'
export const IS_RESET_PASSWORD_KEY = 'isResetPassword'
export const ALLOWED_PLATFORM_KEY = 'allowedPlatform'

// The API returns `0 | 1` but localStorage only stores strings. Coerce on
// read so callers can compare against numeric literals (`=== 1` / `=== 0`).
const toFlag = (raw) => {
  if (raw === null || raw === undefined) return null
  const n = Number(raw)
  return Number.isFinite(n) ? n : null
}

const writeFlag = (key, value) => {
  if (value === null || value === undefined) {
    localStorage.removeItem(key)
    return
  }
  localStorage.setItem(key, String(value))
}

export const loginMetaStorage = {
  // isVerified — has the user completed email/identity verification.
  getIsVerified: () => toFlag(localStorage.getItem(IS_VERIFIED_KEY)),
  setIsVerified: (value) => writeFlag(IS_VERIFIED_KEY, value),
  isVerified: () => toFlag(localStorage.getItem(IS_VERIFIED_KEY)) === 1,

  // isResetPassword — server says the user must rotate their password
  // before they can continue. Drives the forced-rotation guard.
  getIsResetPassword: () => toFlag(localStorage.getItem(IS_RESET_PASSWORD_KEY)),
  setIsResetPassword: (value) => writeFlag(IS_RESET_PASSWORD_KEY, value),
  isResetPasswordRequired: () => toFlag(localStorage.getItem(IS_RESET_PASSWORD_KEY)) === 1,

  // Single-shot write for the full login payload — keeps the call site
  // tidy and avoids partial writes if the API ever returns one but not
  // the other. Missing/undefined values remove the key (no stale data).
  setLoginMeta: ({ isVerified, isResetPassword } = {}) => {
    writeFlag(IS_VERIFIED_KEY, isVerified)
    writeFlag(IS_RESET_PASSWORD_KEY, isResetPassword)
  },

  // Drop every login-response flag. The tokens themselves live in
  // `tokenStorage` — split so callers can wipe one or the other.
  clearLoginMeta: () => {
    localStorage.removeItem(IS_VERIFIED_KEY)
    localStorage.removeItem(IS_RESET_PASSWORD_KEY)
  },

  // allowedPlatform — the array of platforms the backend says this user is
  // licensed for (each entry has at least `slug`). Stored as raw JSON so the
  // full record (name, logoId, etc.) is available without a follow-up API
  // hit; `getAllowedPlatformSlugs` projects the uppercased slug list that
  // `ProductSelection` uses to gate per-user card state.
  getAllowedPlatform: () => {
    const raw = localStorage.getItem(ALLOWED_PLATFORM_KEY)
    if (!raw) return []
    try {
      const parsed = JSON.parse(raw)
      return Array.isArray(parsed) ? parsed : []
    } catch {
      return []
    }
  },
  setAllowedPlatform: (list) => {
    if (!Array.isArray(list)) {
      localStorage.removeItem(ALLOWED_PLATFORM_KEY)
      return
    }
    localStorage.setItem(ALLOWED_PLATFORM_KEY, JSON.stringify(list))
  },
  getAllowedPlatformSlugs: () => {
    const list = loginMetaStorage.getAllowedPlatform()
    return list
      .map((p) => (typeof p?.slug === 'string' ? p.slug.toUpperCase() : null))
      .filter(Boolean)
  },
  clearAllowedPlatform: () => localStorage.removeItem(ALLOWED_PLATFORM_KEY),
}

// One-line shortcuts — matches the `tokenStorage`/`userStorage` style so
// call sites can `import { setLoginMeta, isVerified } from '../utils'`.
export const getIsVerified = () => loginMetaStorage.getIsVerified()
export const setIsVerified = (value) => loginMetaStorage.setIsVerified(value)
export const isVerified = () => loginMetaStorage.isVerified()
export const getIsResetPassword = () => loginMetaStorage.getIsResetPassword()
export const setIsResetPassword = (value) => loginMetaStorage.setIsResetPassword(value)
export const isResetPasswordRequired = () => loginMetaStorage.isResetPasswordRequired()
export const setLoginMeta = (meta) => loginMetaStorage.setLoginMeta(meta)
export const clearLoginMeta = () => loginMetaStorage.clearLoginMeta()
export const getAllowedPlatform = () => loginMetaStorage.getAllowedPlatform()
export const setAllowedPlatform = (list) => loginMetaStorage.setAllowedPlatform(list)
export const getAllowedPlatformSlugs = () => loginMetaStorage.getAllowedPlatformSlugs()
export const clearAllowedPlatform = () => loginMetaStorage.clearAllowedPlatform()
